import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, Heart, User, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/", label: "Home", tid: "nav-link-home" },
  { to: "/shop", label: "Shop", tid: "nav-link-shop" },
  { to: "/shop", label: "Categories", tid: "nav-link-categories" },
  { to: "/offers", label: "Offers", tid: "nav-link-offers" },
  { to: "/about", label: "About", tid: "nav-link-about" },
  { to: "/contact", label: "Contact", tid: "nav-link-contact" },
];

export default function Header() {
  const { itemCount, cartAnimTick, wishlist } = useCart();
  const { user, logout } = useAuth();
  const [bump, setBump] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (cartAnimTick > 0) { setBump(true); const t = setTimeout(() => setBump(false), 500); return () => clearTimeout(t); }
  }, [cartAnimTick]);

  const navClasses = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-[hsl(var(--accent))]" : "text-foreground/80 hover:text-[hsl(var(--accent))]"}`;

  return (
    <header data-testid="header-navigation"
      className="sticky top-0 z-40 backdrop-blur-md bg-[hsl(var(--background))]/85 border-b border-[hsl(var(--border))]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-candle-caramel to-candle-terracotta flex items-center justify-center shadow-md group-hover:shadow-lg transition">
            <span className="w-2 h-3 rounded-full bg-candle-glow shadow-[0_0_10px_#FFC107]" />
          </div>
          <div className="leading-tight">
            <div className="font-serif text-xl sm:text-2xl font-semibold text-[hsl(var(--foreground))]">Candle Co.</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground -mt-1 hidden sm:block">Handpoured Warmth</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((n) => (
            <NavLink key={n.label} to={n.to} end={n.to === "/"} data-testid={n.tid} className={navClasses}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/shop" data-testid="wishlist-link" className="relative p-2 rounded-full hover:bg-[hsl(var(--muted))]">
            <Heart className="w-5 h-5 text-[hsl(var(--foreground))]" />
            {wishlist.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-[hsl(var(--accent))] text-white flex items-center justify-center">{wishlist.length}</span>
            )}
          </Link>

          <Link to="/cart" data-testid="cart-drawer-trigger" className={`relative p-2 rounded-full hover:bg-[hsl(var(--muted))] ${bump ? "cart-bounce" : ""}`}>
            <ShoppingBag className="w-5 h-5 text-[hsl(var(--foreground))]" />
            {itemCount > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-[hsl(var(--accent))] text-white flex items-center justify-center">{itemCount}</span>
            )}
          </Link>

          {user && user !== false ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-trigger" variant="ghost" size="icon" className="rounded-full">
                  <User className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="font-normal">
                  <div className="font-medium">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="user-logout" onClick={logout} className="cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="login-nav-link" className="hidden sm:block">
              <Button variant="ghost" size="sm" className="rounded-full">Sign in</Button>
            </Link>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-trigger" variant="ghost" size="icon" className="lg:hidden rounded-full">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] sm:w-[380px] bg-[hsl(var(--background))]">
              <div className="flex items-center justify-between mb-8">
                <div className="font-serif text-xl">Candle Co.</div>
                <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="flex flex-col gap-1">
                {nav.map((n) => (
                  <NavLink key={n.label} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)}
                    data-testid={`mobile-${n.tid}`}
                    className={({ isActive }) => `px-3 py-3 rounded-md text-lg font-medium ${isActive ? "bg-[hsl(var(--muted))] text-[hsl(var(--accent))]" : "hover:bg-[hsl(var(--muted))]"}`}>
                    {n.label}
                  </NavLink>
                ))}
                {!user || user === false ? (
                  <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-3 mt-3 rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-medium text-center">Sign in</Link>
                ) : null}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
